import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldBan, Plus, Trash2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loading } from "@/components/common/Loading";

export function ReservedWordsManager() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [words, setWords] = useState("");
  const [note, setNote] = useState("");
  const [adding, setAdding] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [refresh, setRefresh] = useState(0);

  const bump = () => setRefresh((k) => k + 1);

  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/reserved-words?q=${encodeURIComponent(q)}`);
      setItems(data.items || []);
    } catch { setItems([]); }
  }, [q]);

  useEffect(() => { reload(); }, [reload, refresh]);

  const add = async () => {
    if (!words.trim()) { toast.error("Enter at least one word."); return; }
    setAdding(true);
    try {
      const { data } = await api.post("/admin/reserved-words", { words: words.trim(), note: note.trim() });
      toast.success(`Added ${data.added_count}${data.skipped.length ? ` · skipped ${data.skipped.length}` : ""}`);
      setWords(""); setNote(""); bump();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add words"); }
    finally { setAdding(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/reserved-words/${deleteTarget.id}`);
      toast.success("Word removed."); setDeleteTarget(null); bump();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-reserved-words">
      <Card className="border-0 p-5">
        <h2 className="flex items-center gap-2 text-lg font-[640] text-foreground"><ShieldBan className="h-5 w-5 text-primary" /> Add reserved words</h2>
        <p className="mt-1 text-xs text-muted-foreground">A reserved word blocks both the matching @handle and the matching slug (every profile type) from being claimed by members. Separate multiple words with commas or new lines.</p>
        <div className="mt-4 space-y-3">
          <Textarea value={words} onChange={(e) => setWords(e.target.value)} rows={2} placeholder="admin, support, login" data-testid="reserved-word-input" />
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="note (optional)" data-testid="reserved-word-note" />
          <Button onClick={add} disabled={adding} data-testid="reserved-word-add">{adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1.5 h-4 w-4" /> Add words</>}</Button>
        </div>
      </Card>

      <Card className="border-0 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-[640] text-foreground">Reserved words {items ? <span className="text-muted-foreground">({items.length})</span> : null}</h2>
          <Input value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Search words…" className="w-full max-w-xs" data-testid="reserved-word-search" />
        </div>
        {!items ? <div className="mt-6"><Loading /></div> : items.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">{q ? "No words match." : "No reserved words yet."}</p>
        ) : (
          <div className="mt-4 flex flex-wrap gap-2">
            {items.map((w) => (
              <Badge key={w.id} variant="secondary" className="group gap-1.5 border border-border bg-secondary py-1 pl-2.5 pr-1.5 text-sm font-[560]" data-testid={`reserved-word-${w.id}`}>
                {w.word}
                <button onClick={() => setDeleteTarget(w)} className="rounded-full p-0.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive" data-testid={`reserved-word-delete-${w.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </Card>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove “{deleteTarget?.word}”?</AlertDialogTitle>
            <AlertDialogDescription>Members will be able to claim @{deleteTarget?.word} and /{deleteTarget?.word} again.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="reserved-word-delete-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
